import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle, ArrowRight } from "lucide-react";

export default function MagicLinkVerify() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { verifyMagicLink } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const token = params.get("token");
        if (!token) {
            setError("Missing or invalid link.");
            return;
        }

        (async () => {
            try {
                const user = await verifyMagicLink(token);
                const next = params.get("next");
                let dest;
                if (next && next.startsWith("/")) {
                    // Internal-only deep link (prevent open redirects)
                    dest = next;
                } else {
                    dest = user.role === "owner" || user.role === "coach" ? "/coach" : "/dashboard";
                }
                navigate(dest, { replace: true });
            } catch (e) {
                const msg =
                    e?.response?.data?.detail ||
                    "This sign-in link is invalid or has expired.";
                setError(msg);
            }
        })();
    }, [params, verifyMagicLink, navigate]);

    return (
        <div
            data-testid="magic-verify-page"
            className="min-h-screen bg-brand-obsidian text-white flex flex-col items-center justify-center px-6"
        >
            {!error ? (
                <>
                    <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
                    <div className="mt-5 text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                        Verifying your link
                    </div>
                </>
            ) : (
                <div
                    data-testid="magic-verify-error"
                    className="max-w-md w-full text-center border border-zinc-900 bg-zinc-950 p-10"
                >
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-red/10 border border-brand-red/40 text-brand-red mb-5">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3 uppercase">
                        Link not valid
                    </h1>
                    <p className="text-zinc-200 text-sm leading-relaxed">{error}</p>
                    <Link to="/" data-testid="magic-verify-back" className="btn-tactical mt-7 inline-flex">
                        Back to site <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            )}
        </div>
    );
}
